import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import type { SpeakingPart } from '../../types';

/** Editable form of the three parts; blank parts are dropped on save. */
export interface SpeakingDraft {
  part1: string;
  part1Seconds: number;
  cueCard: string;
  followUp: string;
  prepSeconds: number;
  talkSeconds: number;
  part3: string;
  part3Seconds: number;
}

const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

export function speakingDraftFrom(parts: SpeakingPart[] | undefined): SpeakingDraft {
  const byPart = (n: 1 | 2 | 3) => parts?.find((p) => p.part === n);
  const p1 = byPart(1);
  const p2 = byPart(2);
  const p3 = byPart(3);
  return {
    part1: (p1?.prompts ?? []).join('\n'),
    part1Seconds: p1?.answerSeconds ?? 30,
    cueCard: p2?.prompts[0] ?? '',
    followUp: p2?.prompts[1] ?? '',
    prepSeconds: p2?.prepSeconds ?? 60,
    talkSeconds: p2?.answerSeconds ?? 120,
    part3: (p3?.prompts ?? []).join('\n'),
    part3Seconds: p3?.answerSeconds ?? 60,
  };
}

export function speakingPartsFrom(d: SpeakingDraft): SpeakingPart[] {
  const parts: SpeakingPart[] = [];
  if (lines(d.part1).length) parts.push({ part: 1, prompts: lines(d.part1), answerSeconds: d.part1Seconds });
  if (d.cueCard.trim()) {
    parts.push({
      part: 2,
      prompts: [d.cueCard.trim(), ...(d.followUp.trim() ? [d.followUp.trim()] : [])],
      prepSeconds: d.prepSeconds,
      answerSeconds: d.talkSeconds,
    });
  }
  if (lines(d.part3).length) parts.push({ part: 3, prompts: lines(d.part3), answerSeconds: d.part3Seconds });
  return parts;
}

/** What stops the Speaking test from saving; null when it can be saved. */
export function speakingDraftIssue(d: SpeakingDraft): string | null {
  if (!speakingPartsFrom(d).length) return 'exam.speaking.need_a_part';
  const bad = (n: number, min: number, max: number) => !Number.isInteger(n) || n < min || n > max;
  if (bad(d.part1Seconds, 10, 300) || bad(d.talkSeconds, 10, 300) || bad(d.part3Seconds, 10, 300)) return 'exam.speaking.time_range';
  if (bad(d.prepSeconds, 0, 180)) return 'exam.speaking.prep_range';
  return null;
}

interface Props {
  value: SpeakingDraft;
  onChange: (next: SpeakingDraft) => void;
}

/** The three IELTS Speaking parts: questions one per line, the Part 2 cue card, and timings. */
export function SpeakingEditor({ value, onChange }: Props) {
  const { t } = useTranslation();
  const set = (patch: Partial<SpeakingDraft>) => onChange({ ...value, ...patch });
  const seconds = (label: string, field: keyof SpeakingDraft, min: number, max: number) => (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Input
        type="number"
        min={min}
        max={max}
        value={value[field] as number}
        onChange={(e) => set({ [field]: Math.round(Number(e.target.value)) } as Partial<SpeakingDraft>)}
        className="h-10 w-28"
      />
    </label>
  );
  const textarea = 'min-h-[120px] w-full rounded-lg border border-input bg-background p-3 text-sm leading-relaxed focus:outline-none focus-visible:!outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="space-y-4">
      <section className="space-y-3 rounded-xl border border-border p-4">
        <div>
          <h3 className="font-display text-base font-bold">{t('exam.speaking.part1')}</h3>
          <p className="text-xs text-muted-foreground">{t('exam.speaking.part1_help')}</p>
        </div>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">{t('exam.speaking.questions_one_per_line')}</span>
          <textarea
            className={textarea}
            value={value.part1}
            onChange={(e) => set({ part1: e.target.value })}
            placeholder={'Do you work or are you a student?\nWhat do you enjoy most about your studies?'}
          />
        </label>
        {seconds(t('exam.speaking.answer_seconds'), 'part1Seconds', 10, 300)}
      </section>

      <section className="space-y-3 rounded-xl border border-border p-4">
        <div>
          <h3 className="font-display text-base font-bold">{t('exam.speaking.part2')}</h3>
          <p className="text-xs text-muted-foreground">{t('exam.speaking.part2_help')}</p>
        </div>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">{t('exam.speaking.cue_card')}</span>
          <textarea
            className={textarea}
            value={value.cueCard}
            onChange={(e) => set({ cueCard: e.target.value })}
            placeholder={'Describe a place in your city that you enjoy visiting.\nYou should say:\nwhere it is\nhow often you go there\nwhat you do there\nand explain why you enjoy visiting it.'}
          />
        </label>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">{t('exam.speaking.follow_up')}</span>
          <Input value={value.followUp} onChange={(e) => set({ followUp: e.target.value })} placeholder="Do you think you'll still go there in ten years?" />
        </label>
        <div className="flex flex-wrap gap-4">
          {seconds(t('exam.speaking.prep_seconds'), 'prepSeconds', 0, 180)}
          {seconds(t('exam.speaking.talk_seconds'), 'talkSeconds', 10, 300)}
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-border p-4">
        <div>
          <h3 className="font-display text-base font-bold">{t('exam.speaking.part3')}</h3>
          <p className="text-xs text-muted-foreground">{t('exam.speaking.part3_help')}</p>
        </div>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">{t('exam.speaking.questions_one_per_line')}</span>
          <textarea
            className={textarea}
            value={value.part3}
            onChange={(e) => set({ part3: e.target.value })}
            placeholder={'Why do cities need more public spaces?\nHow have parks changed in your country?'}
          />
        </label>
        {seconds(t('exam.speaking.answer_seconds'), 'part3Seconds', 10, 300)}
      </section>
    </div>
  );
}

export default SpeakingEditor;
